import { mobile } from "./detection";
import { isLoggedInWithoutRole } from "./";
import { contentAdTypes, debugEnvs, getLangCode, gotwListShortname, language, motionGames, userRoles } from "../config";
import store from "../redux/store";
import { gameCategories, gradeCategories } from "../config/categories";
import { routeCodes } from "../config/routes";

export const gameCanPlay = (free) => {
  // Will return true if a game is availble to play
  // game is Free or user is premium
  return isLoggedInWithoutRole(userRoles.free) || free;
};

export const getGOTWList = (listArray = []) => {
  return listArray.find((list) => {
    return list.shortname === gotwListShortname && list.group === "free";
  });
};

export const isFreeGame = (ls = [], id) => {
  const gotwList = getGOTWList(ls);

  const freeGamesOfTheWeek = gotwList ? gotwList.items.map((item) => (item.game ? item.game : "")) : [];
  return !!freeGamesOfTheWeek.find((g) => g === id);
};

export const getMobileHomeStyle = (corner, offX, offY, buttAlign) => {
  const styleObj = {};
  let classString = "";

  if (corner.startsWith("TOP")) {
    if (offY !== 0) {
      styleObj.top = `${offY}px`;
    }
  }
  if (corner.startsWith("BOTTOM")) {
    classString += " bottom";
    if (offY !== 0) {
      styleObj.bottom = `${offY}px`;
    }
  }
  if (corner.endsWith("LEFT")) {
    if (offX !== 0) {
      styleObj.left = `${offX}px`;
    }
  }
  if (corner.endsWith("RIGHT")) {
    classString += " right";
    if (offX !== 0) {
      styleObj.right = `${offX}px`;
    }
  }
  if (buttAlign === "vertical" || buttAlign === "portrait") {
    classString += " vertical";
  }

  return {
    class: classString,
    style: styleObj,
  };
};

export const fullScreen = {
  go: (elem) => {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  },
  exit: () => {
    if (document.exitFullscreen && document.fullscreenElement) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen && document.webkitFullscreenElement) {
      document.webkitExitFullscreen();
    } else if (document.mozExitFullScreen && document.mozFullscreenElement) {
      document.mozExitFullScreen();
    } else if (document.msExitFullscreen && document.msFullscreenElement) {
      document.msExitFullscreen();
    }
  },
};

export const getAllGames = (data) => {
  return data.filter((game) => game.searchable && (!mobile || (mobile && game.mobileActive)));
};

export const getGamesByGradeNumber = (games, gradeNum) => {
  const allGames = getAllGames(games);
  return allGames.reduce((gradeGamesArray, game) => {
    if (game.grades.includes(gradeNum)) {
      gradeGamesArray.push(game);
    }
    return gradeGamesArray;
  }, []);
};

const getGameObjectFromLocation = (location) => {
  const {
    data: { games },
  } = store().getState().data;

  const delim = "/";
  const pathnameArray = location.pathname.split(delim);
  const shortNameIndex = location.pathname.endsWith(delim) ? pathnameArray.length - 2 : pathnameArray.length - 1;
  const shortname = pathnameArray[shortNameIndex];
  return games.find((game) => game.localeShortname === shortname || game.shortname === shortname);
};

export const getPauseAfterPlayStatus = (location) => {
  const isGameProxy = location.pathname.startsWith("/gameproxy");
  const gameObject = getGameObjectFromLocation(location);

  let pauseAfterPlay = false;

  if (gameObject?.adType) {
    const { ads } = store().getState().login;
    console.log("ads: ", ads);
    console.log("gameObject.adType: ", gameObject.adType);

    pauseAfterPlay = ads && !mobile && gameObject.adType === contentAdTypes.clickToPlay && !isGameProxy;
  }

  return pauseAfterPlay;
};

export const getGameParentData = (location) => {
  const languageCode = getLangCode();

  // determine click to play flag based on game shortname and test game list.  send back in response as config object
  const pauseAfterPlay = getPauseAfterPlayStatus(location);

  console.log("pauseAfterPlay: ", pauseAfterPlay);
  console.log("getGameParentData location: ", location);

  const parent = {
    name: "abcya-web",
    env: process.env.NODE_ENV,
    capabilities: {
      canPrintImages: true,
      canSaveImages: true,
    },
    config: {
      pauseAfterPlay,
      analyticsEnabled: true,
      debugMode: false,
    },
  };

  const searchParams = new URLSearchParams(location.search);

  if (debugEnvs.includes(process.env.NODE_ENV) && searchParams.has("debug")) {
    parent.config.debugMode = true;
  }

  if (location.pathname.startsWith("/gameproxy")) {
    const contentLanguageValue = searchParams.get("content-language")?.trim();
    const variantName = searchParams.get("variant")?.trim();

    if (contentLanguageValue) {
      parent.contentLanguage = contentLanguageValue;
    }

    if (variantName) {
      parent.variant = { id: variantName };
    }
  } else {
    const contentLanguage =
      languageCode in language.codes ? language.codes[languageCode] : language.codes[language.default];
    parent.contentLanguage = contentLanguage.replace("_", "-");
  }

  if (process.env.NODE_ENV !== "production" && location.hash) {
    // check for test hash
    const testKey = "gameparams";
    const testFlag = `#${testKey}`;

    if (location.hash.startsWith(testFlag)) {
      const testPropsArray = location.hash.split(",");
      if (testPropsArray && testPropsArray.length > 1) {
        // index 0 should always be `#test or #test,`

        // establish test object
        const params = {};

        testPropsArray.forEach((p) => {
          // iterate through all props in array
          if (!p.startsWith(testFlag)) {
            // don't include the test flag
            // parse key and value pairs
            const pArray = p.split("=");
            if (pArray.length === 2) {
              // only add if properly formatted
              // key index 0, value index 1, length 2
              // add them to parent data
              params[pArray[0]] = pArray[1];
            }
          }
        });

        if (Object.keys(params).length > 0) {
          parent[testKey] = params;
        }
      }
    }
  }
  return parent;
};

export const getGameHTMLVersion = (game, gameVersionOverride) => {
  // "gameVersion" query param is only available in the testing/staging environments.
  if (process.env.NODE_ENV !== "production" && typeof gameVersionOverride === "string") {
    return gameVersionOverride ? `html-${gameVersionOverride}` : "html";
  }
  if (game.abTestGameVersion) {
    return `html-${game.abTestGameVersion}`;
  }
  return "html";
};

export const getGameAnalytics = (game, isMobile) => {
  const genre = [];
  const subject = [];
  const theme = [];
  const gameCatSet = new Set(game.categories);
  gameCategories.forEach((category) => {
    switch (category.type) {
      case "genre":
        if (gameCatSet.has(category.shortname)) {
          genre.push(category.name);
        }
        break;
      case "subject":
        if (gameCatSet.has(category.shortname)) {
          subject.push(category.name);
        }
        break;
      case "theme":
        if (gameCatSet.has(category.shortname)) {
          theme.push(category.name);
        }
        break;
    }
  });
  let category = "";
  for (let i = 0; i < gradeCategories.length; ++i) {
    const cat = gradeCategories[i];
    if (gameCatSet.has(cat.shortname)) {
      category = cat.shortname;
      break;
    }
  }
  const gamePageCurrentURL = `${window.location.protocol}//${window.location.host}${isMobile ? routeCodes.MOBILEGAMES : routeCodes.GAMES}/${game.shortname}`;
  return { id: game.id, genre, subject, theme, category, fullname: game.fullname, gamePageCurrentURL };
};

export const requiresMotion = (shortname) => {
  return (
    motionGames.includes(shortname) &&
    window.location.protocol === "https:" &&
    DeviceMotionEvent &&
    typeof DeviceMotionEvent.requestPermission === "function"
  );
};
